import { Clear } from "@mui/icons-material";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import {
  Autocomplete,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Box,
  Typography,
  Link,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ClientAutoComplete from "components/Common/AutoCompletes/ClientAutoComplete";
import SiteAutoComplete from "components/Common/AutoCompletes/SiteAutoComplete";
import { format } from "date-fns";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import {
  getOBTInfoManagementByProjectCode,
  getProjectsByClientCode,
  getTechDepType,
} from "services/userServices";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import { red } from "@mui/material/colors";
import BackdropComponent from "components/Spinner/Spinner";
import PropTypes from "prop-types";

const fieldsPadding = { p: 1, pl: 2 };
const today = moment();

export const StyledChip = styled(Chip)(() => ({
  borderRadius: "4px",
  whiteSpace: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

export const LimitChip = styled(Chip)(() => ({
  fontWeight: "bold",
  color: "#3299FF",
}));

export default function NoRosterIdCreateUpdateIdmRecord(props) {
  const { updateRosterDetails, rosterDetails, isEdit, idmChecklistURL } = props;
  const [projects, setProjects] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [selectedSite, setSelectedSite] = React.useState(null);
  const [selectedProjects, setSelectedProjects] = React.useState([]);
  const [techDeploymentTypes, setTechDeploymentTypes] = React.useState([]);
  const [selectedTechDeploymentType, setSelectedTechDeploymentType] =
    useState(null);
  const [headCount, setHeadCount] = React.useState(null);
  const [rosterLockDate, setRosterLockDate] = useState(null);
  const [trainingStartDate, setTrainingStartDate] = useState(null);
  const [idmTeamLead, setIdmTeamLead] = useState(null);
  const [primaryIdmPoc, setPrimaryIdmPoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  // Tracks whether we still owe the one-time population of selectedProjects
  // from the loaded record. Only true when opening an existing record for edit.
  // Once the initial hydration completes, this flips to false and neither the
  // client-change effect nor the projects-repopulate effect are allowed to
  // overwrite the user's live selection again.
  const isHydratingRef = useRef(isEdit === true);
  const prepareIdmNames = (data, key) => {
    return [...new Set(data?.map((item) => item[key]))]
      ?.join(", ")
      .replace(/, $/, "");
  };

  useEffect(() => {
    if (rosterDetails && isEdit === true) {
      setHeadCount(rosterDetails?.NumberOfAssignedTrainees || null);
      setRosterLockDate(rosterDetails?.RosterLockDate || null);
      setTrainingStartDate(rosterDetails?.ProductTrainingDate || null);
      setIdmTeamLead(rosterDetails?.IDMTeamLead || null);
      setPrimaryIdmPoc(rosterDetails?.PrimaryIDMPOC || null);
    }
  }, [rosterDetails, isEdit]);

  useEffect(() => {
    updateRosterDetails({
      selectedSite: selectedSite,
      selectedClient: selectedClient,
      selectedProjects: selectedProjects,
      selectedTechDeploymentType: selectedTechDeploymentType,
      headCount: headCount,
      rosterLockDate: rosterLockDate,
      trainingStartDate: trainingStartDate,
      idmTeamLead: idmTeamLead,
      primaryIdmPoc: primaryIdmPoc,
    });
  }, [
    selectedSite,
    selectedClient,
    selectedProjects,
    selectedTechDeploymentType,
    headCount,
    rosterLockDate,
    trainingStartDate,
    idmTeamLead,
    primaryIdmPoc,
  ]);

  const fetchIdmDetails = async (projects) => {
    setLoading(true);
    const projectCodes = projects.map((project) => project.ProjectCode);
    const response = await getOBTInfoManagementByProjectCode(projectCodes);

    if (response.message === "Success") {
      setIdmTeamLead(prepareIdmNames(response.data, "IDMTeamLead"));
      setPrimaryIdmPoc(prepareIdmNames(response.data, "PrimaryIDMPOC"));
    }
    setLoading(false);
  };

  const handleProjectChange = (event, newValue) => {
    if (newValue) {
      setSelectedProjects(newValue);
      if (newValue.length > 0) {
        setIdmTeamLead(null);
        setPrimaryIdmPoc(null);
        fetchIdmDetails(newValue);
      }
    }
  };

  const handleTechDeploymentChange = (event, newValue) => {
    setSelectedTechDeploymentType(newValue);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchTechDeploymentTypes = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const response = await getTechDepType();

        if (cancelled) return;

        if (response.message === "Success") {
          if (!cancelled) {
            setTechDeploymentTypes(response.data);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch tech deployment types:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTechDeploymentTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchProjects = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const response = await getProjectsByClientCode(
          selectedClient?.ClientCode,
        );

        if (cancelled) return;

        if (response.message === "Success") {
          if (!cancelled) {
            // Guardrail: exclude inactive/retired project rows so a broken
            // duplicate (e.g. a legacy migrated project with Status=0) can
            // never be selected again in the first place.
            setProjects(
              response.data.filter((project) => project.Status === true),
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch projects:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Only clear the current selection for a genuine client change made by the
    // user. While we're still hydrating from a loaded record, selectedClient
    // is being set programmatically (possibly more than once, e.g. once
    // unresolved then once resolved) and must not wipe out the project list
    // that's about to be populated from the loaded record.
    if (!isHydratingRef.current) {
      setSelectedProjects([]);
    }

    if (selectedClient?.ClientCode) {
      fetchProjects();
    }

    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  useEffect(() => {
    // Hydrate selectedProjects from the loaded record exactly once. Without
    // this guard, any later reference change to `projects` (e.g. a spurious
    // re-fire from the Client/Site autocompletes, a StrictMode double-invoke,
    // or a future re-render) would re-derive selectedProjects from this
    // static, original ProjectName string and silently discard whatever the
    // user has since added or removed in the multi-select.
    if (
      rosterDetails?.Project &&
      isEdit === true &&
      isHydratingRef.current &&
      projects.length > 0
    ) {
      // Match by ProjectCode, not ProjectName. Two distinct Project rows can
      // share an identical ProjectName (e.g. a legacy duplicate), which would
      // cause a name-based match to re-select a project that was deliberately
      // removed and re-saved. rosterDetails.Project is the code-qualified
      // field, e.g. "Some Project Name (2515112), Other Project (2515017)".
      const onLoadProjectCodes = [...rosterDetails.Project.matchAll(/\(([^()]+)\)/g)].map(
        (match) => match[1].trim(),
      );
      const selectedProjects = projects.filter((project) =>
        onLoadProjectCodes.includes(project.ProjectCode),
      );
      setSelectedProjects(selectedProjects);
      isHydratingRef.current = false;
    }
  }, [projects, rosterDetails?.Project, isEdit]);

  useEffect(() => {
    if (rosterDetails?.AskNowTDT && isEdit === true) {
      const selectedTechDeploymentType = techDeploymentTypes.find(
        (techDeploymentType) =>
          techDeploymentType.AskNowTDT === rosterDetails?.AskNowTDT,
      );
      setSelectedTechDeploymentType(selectedTechDeploymentType);
    }
  }, [techDeploymentTypes, rosterDetails?.AskNowTDT, isEdit]);

  return (
    <>
      {(loading || sitesLoading || clientsLoading) && <BackdropComponent />}
      <Grid container>
        <Grid item xs={3} sx={fieldsPadding}>
          <SiteAutoComplete
            setLoading={(value) => setSitesLoading(value)}
            onChange={(value) => {
              setSelectedSite(value);
            }}
            {...(isEdit === true && { onLoadValue: rosterDetails?.SiteName })}
            required
          />
        </Grid>
        <Grid item xs={4.5} sx={fieldsPadding}>
          <ClientAutoComplete
            setLoading={(value) => setClientsLoading(value)}
            onChange={(value) => {
              setSelectedClient(value);
            }}
            {...(isEdit === true && { onLoadValue: rosterDetails?.ClientName })}
            required
          />
        </Grid>
        <Grid item xs={4.5} sx={fieldsPadding}>
          <Autocomplete
            multiple
            disablePortal
            options={projects}
            value={selectedProjects}
            onChange={handleProjectChange}
            getOptionLabel={(option) =>
              option.ProjectName
                ? `${option.ProjectName} (${option.ProjectCode})`
                : ""
            }
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={
                  <span>
                    Project{" "}
                    <Box component={"span"} sx={{ color: red[700] }}>
                      *
                    </Box>
                  </span>
                }
                variant="outlined"
                sx={{ backgroundColor: "#fff" }}
              />
            )}
            renderTags={(value, getTagProps) => {
              const limitTags = 1;
              const more = value.length - limitTags;
              return (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <StyledChip
                      label={`${option.ProjectName} (${option.ProjectCode})`}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))}
                  {more > 0 && <LimitChip label={`+${more}`} size="small" />}
                </>
              );
            }}
          />
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={
              <span>
                HeadCount{" "}
                <Box component={"span"} sx={{ color: red[700] }}>
                  *
                </Box>
              </span>
            }
            value={headCount}
            inputProps={{ min: 1 }}
            onChange={(e) => {
              if (e.target.value > 0 || e.target.value === "") {
                setHeadCount(e.target.value);
              }
            }}
          />
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <Autocomplete
            options={techDeploymentTypes}
            value={selectedTechDeploymentType}
            onChange={handleTechDeploymentChange}
            getOptionLabel={(option) =>
              option.AskNowTDT ? option.AskNowTDT : ""
            }
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={
                  <span>
                    Tech Deployment Type{" "}
                    <Box component={"span"} sx={{ color: red[700] }}>
                      *
                    </Box>
                  </span>
                }
                variant="outlined"
                sx={{ backgroundColor: "#fff" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={moment(rosterLockDate) || null}
              onChange={(newValue) => {
                if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                  setRosterLockDate(format(newValue, "yyyy-MM-dd"));
                }
              }}
              format="yyyy-MM-dd"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={false}
                  fullWidth
                  variant="outlined"
                  label={
                    <span>
                      Roster Locked Date{" "}
                      <Box component={"span"} sx={{ color: red[700] }}>
                        *
                      </Box>
                    </span>
                  }
                  name="Roster Locked Date"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {rosterLockDate && (
                          <Tooltip title={"Clear"}>
                            <IconButton
                              onClick={() => setRosterLockDate(today)}
                              edge="end"
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={moment(trainingStartDate) || null}
              onChange={(newValue) => {
                if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                  setTrainingStartDate(format(newValue, "yyyy-MM-dd"));
                }
              }}
              format="yyyy-MM-dd"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  error={false}
                  variant="outlined"
                  label={
                    <span>
                      Training Start Date{" "}
                      <Box component={"span"} sx={{ color: red[700] }}>
                        *
                      </Box>
                    </span>
                  }
                  name="Training Start Date"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {trainingStartDate && (
                          <Tooltip title={"Clear"}>
                            <IconButton
                              onClick={() => setTrainingStartDate(today)}
                              edge="end"
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <TextField
            fullWidth
            size="small"
            label="IDM Team Lead"
            value={idmTeamLead || "NA"}
            {...(idmTeamLead !== null && { disabled: true })}
          />
        </Grid>
        <Grid item xs={3} sx={fieldsPadding}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            label="Primary IDM POC"
            value={primaryIdmPoc || "NA"}
            {...(primaryIdmPoc !== null && { disabled: true })}
          />
        </Grid>
        <Grid
          item
          xs={3}
          sx={{
            textAlign: "left",
            fontWeight: "bold",
            marginLeft: 1,
          }}
        >
          {idmChecklistURL.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "flex-end" }}>
              <Tooltip title="IDM Checklist">
                {isEdit && (
                  <Link
                    href={idmChecklistURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      textDecoration: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist, sans-serif",
                        fontWeight: "bold",
                      }}
                    >
                      IDM Checklist
                    </Typography>
                    <SimCardDownloadOutlinedIcon sx={{ fontSize: "2rem" }} />
                  </Link>
                )}
              </Tooltip>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}

NoRosterIdCreateUpdateIdmRecord.propTypes = {
  updateRosterDetails: PropTypes.func.isRequired,
  rosterDetails: PropTypes.any,
  isEdit: PropTypes.bool,
  idmChecklistURL: PropTypes.string,
};
